import * as React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import {Box, Button, CardActions, Chip, Divider} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import VideoPlacementDto from '../models/dtos/VideoPlacementDto';
import VideoRewardOptionsDto from '../models/dtos/VideoRewardOptionsDto';
import HomeMatchAdDto, {HomeMatchAdLinkType} from "../models/dtos/HomeMatchAdDto";

interface HomeMatchAdsProps {
    item: HomeMatchAdDto,
    showEdit: boolean,
    onDelete?: () => void
}

const getRewardDescription = (options: VideoRewardOptionsDto) => {
    let description = '';

    if (options.type == 'Betcoins') {
        if (options.definedValue) {
            description = 'User receives ' + options.definedValue + ' Betcoins'
        } else {
            description = 'User receives x' + options.multiplier + ' of the referenced value (defined by the placement context)';
            if (options.continueForMaxAmountExceeded) {
                description += ', but not greater than ' + options.maxAmount + ' Betcoins';
            } else {
                description += ', if the possible calculated reward exceeds ' + options.maxAmount + ' Betcoins, video offer will not be avilable';
            }
        }
    } else {
        description = 'User receives the following bonus: ' + options.type;
    }

    return description;
}

const getTimingDescription = (item: VideoPlacementDto) => {
    let description = '';

    if (item.oneTimeOffer) {
        description = 'This offer is available only once (1 time / user)'
    } else {
        description = `This offer is available not more than ${item.maxTimesPerInterval} times each ${item.intervalInHours} hours`
    }

    return description;
}

const getStatsDescription = (item: VideoPlacementDto) => {
    return `${item.weeklyViews} views from ${item.weeklyUsers} users, paid: ${item.weeklyReward} Betcoins`;
}

export default function HomeMatchAdsCard(model: HomeMatchAdsProps) {
    const navigate = useNavigate()
    const isExpired = model.item.expirationDate != null && new Date(model.item.expirationDate) < new Date();
    const opacity = model.item.isActive ? 1 : 0.5;

    return (
        <Card sx={{width: '100%', opacity: opacity}}>
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                background: model.item.isActive ? 'lightBlue' : '#d3d3d3',
                paddingLeft: '16px',
                paddingRight: '16px',
                minHeight: '100px'
            }}>
                <img src='https://cdn-icons-png.freepik.com/256/2859/2859705.png' width='32px' height='32px'/>
                <Typography variant="h5" color="text.secondary" sx={{color: 'black', marginLeft: '32px'}}>
                    {model.item.name}
                </Typography>
                <Box sx={{marginLeft: 'auto', display: 'flex', gap: 1}}>
                    <Chip label={`Priority: ${model.item.priority}`} size="small" />
                    {!model.item.isActive && (
                        <Chip 
                            label={isExpired ? "Expired" : "Inactive"} 
                            size="small" 
                            color={isExpired ? "error" : "warning"}
                        />
                    )}
                </Box>
            </Box>
            <Divider/>
            <CardContent sx={{minHeight: 350}}>
                <React.Fragment>
                    <br/>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '5px',
                        paddingLeft: '16px',
                        paddingRight: '16px',
                        minHeight: '100px'
                    }}>
                        <img src={model.item.imageUrl} style={{maxWidth: '300px'}}/>
                    </Box>
                    <br/>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        borderRadius: '5px',
                        borderStyle: 'solid',
                        paddingLeft: '16px',
                        paddingRight: '16px',
                        minHeight: '100px'
                    }}>
                        <Typography variant="h6">{model.item.linkName}</Typography><br/>
                    </Box>
                    <br/>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        borderRadius: '5px',
                        borderStyle: 'solid',
                        paddingLeft: '16px',
                        paddingRight: '16px',
                        minHeight: '100px'
                    }}>
                        <Typography variant="h6">{HomeMatchAdLinkType[model.item.linkType]}</Typography><br/>
                    </Box>
                    <br/>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderRadius: '5px',
                        borderStyle: 'solid',
                        paddingLeft: '16px',
                        paddingRight: '16px',
                        minHeight: '60px'
                    }}>
                        <Typography variant="body1">
                            <strong>Status:</strong> {model.item.isActive ? 
                                <Chip label="Active" size="small" color="success" sx={{ml: 1}}/> : 
                                <Chip label={isExpired ? "Expired" : "Inactive"} size="small" color={isExpired ? "error" : "warning"} sx={{ml: 1}}/>
                            }
                        </Typography>
                    </Box>
                    <br/>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        borderRadius: '5px',
                        borderStyle: 'solid',
                        paddingLeft: '16px',
                        paddingRight: '16px',
                        minHeight: '60px'
                    }}>
                        <Typography variant="body1">
                            <strong>Expiration Date:</strong> {model.item.expirationDate ? 
                                new Date(model.item.expirationDate).toLocaleString() : 
                                'No expiration'
                            }
                        </Typography>
                    </Box>
                    <Divider/>
                </React.Fragment>
                <br/>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right' }}>
                    {model.item.views} views · {model.item.clicks} clicks
                </Typography>
            </CardContent>
            <CardActions>
                {model.showEdit ?
                    <React.Fragment>
                        {model.onDelete && (
                            <Button size="small" color="error" variant="outlined"
                                    onClick={model.onDelete}>
                                Delete
                            </Button>
                        )}
                        <Button size="small" color="primary" variant="contained"
                                sx={{marginLeft: 'auto', color: 'white'}}
                                onClick={e => navigate('/home-match-ads/' + model.item.id + '/edit')}>
                            Edit
                        </Button>
                    </React.Fragment>
                    : <React.Fragment/>
                }
            </CardActions>
        </Card>
    );
}
